import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import { Alert, AlertTitle, Button, Link as MuiLink, Stack } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { currentSpaPath } from '@/shared/api';
import { useSession } from '@/shared/session';

import {
  hasPendingPasskeyEnrolment,
  markNudgeAnswered,
  readNudgeAnswered,
  startPasskeyEnrolment,
} from './passkeyEnrolment';
import { hasWebAuthnSupport } from './useWebAuthnCapability';

/**
 * The once-per-device post-login nudge (user-story-merchant-portal-060, adr-merchant-portal-020 D5).
 *
 * Mounted exactly once, in `AppLayout`, above `<Outlet/>`. An inline, dismissable `Alert` — not a
 * `Dialog` (must never block) and not the global `Snackbar` (holds one message and auto-hides in 6s,
 * too fragile for an opt-in decision and liable to clobber `StepUpReplay`'s own message).
 *
 * Every one of the trigger conditions below is required — this is deliberately conservative:
 *
 * - `session.isAuthenticated` — nothing to nudge before the session exists.
 * - `session.passkeyEnrolled === false` — a **definite** "not enrolled". `null` (the hint is absent) is
 *   NOT treated as `false`: "never shown to someone already enrolled" cannot be honoured without
 *   knowing, so an absent hint suppresses the nudge entirely — the card remains the entry point.
 * - `hasWebAuthnSupport()` — no point inviting a browser that cannot do this at all.
 * - not answered yet on this device (`readNudgeAnswered`, `localStorage` — permanent once answered).
 * - not already on `/account/*` — the account-security page is the fuller explanation; the nudge would
 *   be redundant chrome on top of the very card it points at.
 * - no enrolment currently pending (`hasPendingPasskeyEnrolment`) — the user just came back from
 *   Keycloak and a consumer elsewhere (the page, or `PasskeyEnrolmentReturn`) still owns reporting that
 *   outcome; the nudge must not race it or paper over it.
 */
export function PasskeyNudgeBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const session = useSession();
  // Lazily seeded from the persisted record, then flipped locally the instant either button is
  // clicked — independent of whether the write to localStorage itself succeeds (private browsing /
  // enterprise policy may block it), so the banner still behaves as answered for this document even
  // when the answer cannot survive a reload (design doc §10).
  const [dismissed, setDismissed] = useState(() => readNudgeAnswered());

  const onAccountPage =
    location.pathname === '/account' || location.pathname.startsWith('/account/');

  const shouldShow =
    session.isAuthenticated &&
    session.passkeyEnrolled === false &&
    !dismissed &&
    !onAccountPage &&
    hasWebAuthnSupport() &&
    !hasPendingPasskeyEnrolment();

  if (!shouldShow) {
    return null;
  }

  const handleNotNow = () => {
    markNudgeAnswered();
    setDismissed(true);
  };

  const handleSetUp = () => {
    markNudgeAnswered();
    setDismissed(true);
    startPasskeyEnrolment({
      source: 'nudge',
      // Belt-and-braces: if the cached hint is stale (enrolled in the console this session),
      // Keycloak answers `success` with no prompt instead of re-registering a credential.
      skipIfExists: true,
      returnPath: currentSpaPath(),
      enrolledBefore: session.passkeyEnrolled,
    });
  };

  return (
    <Alert
      severity="info"
      role="status"
      icon={<FingerprintRoundedIcon />}
      sx={{
        mx: { xs: 2, md: 3 },
        mt: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        '& .MuiAlert-action': { pl: 0, pt: { xs: 1, sm: 0 } },
      }}
      action={
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={handleSetUp}>
            {t('passkeyNudge.setUp')}
          </Button>
          <Button size="small" color="inherit" onClick={handleNotNow}>
            {t('passkeyNudge.notNow')}
          </Button>
        </Stack>
      }
    >
      <AlertTitle>{t('passkeyNudge.title')}</AlertTitle>
      {t('passkeyNudge.message')}{' '}
      <MuiLink component={RouterLink} to="/account/security" sx={{ fontWeight: 600 }}>
        {t('passkeyNudge.learnMore')}
      </MuiLink>
    </Alert>
  );
}
